import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  BarChart3,
  Briefcase,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ClipboardCheck,
  ClipboardList,
  Code,
  Crown,
  Database,
  DollarSign,
  Factory,
  Gauge,
  Globe,
  GraduationCap,
  Handshake,
  LogOut,
  Map as MapIcon,
  MapPin,
  Megaphone,
  Network,
  Route,
  Settings,
  ShoppingCart,
  Target,
  TrendingUp,
  UserCheck,
  UserCircle,
  UserPlus,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { signOut, useSession } from "@/auth/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { getAllowedTeamPaths, isGuest } from "@/lib/team-access";
import { TERRITORIES } from "@/lib/territories";
import { cn } from "@/lib/utils";
import { useSidebar } from "./sidebar-context";

const adminMainNav = [
  { to: "/executive", label: "Executive", icon: Crown },
  { to: "/client-journey", label: "Client Journey", icon: Route },
  { to: "/deal-metrics", label: "Deal Metrics", icon: BarChart3 },
  { to: "/org-chart", label: "Org Chart", icon: Network },
] as const;

const userMainNav = [{ to: "/my-dashboard", label: "My Dashboard", icon: BarChart3 }] as const;

const orgChartNavItem = { to: "/org-chart", label: "My Team", icon: Network } as const;

const fpConsultantsChildren = [
  {
    to: "/teams/fp/consultants/non-relational",
    label: "Non Relational Consultant",
    icon: Handshake,
  },
  { to: "/teams/fp/consultants/relational", label: "Relational Consultant", icon: Handshake },
  { to: "/teams/fp/consultants/wedding-ring", label: "Wedding Ring Consultant", icon: Handshake },
] as const;

const fpTeamNav = [
  { to: "/teams/fp/development", label: "FP Development", icon: Code },
  { to: "/teams/fp/marketing", label: "FP Marketing", icon: Megaphone },
  {
    to: "/teams/fp/consultants",
    label: "FP Consultants",
    icon: Handshake,
    children: fpConsultantsChildren,
  },
  { to: "/teams/fp/operations", label: "FP Operations", icon: ClipboardCheck },
] as const;

const createdTeamNav = [
  { to: "/teams/created/development", label: "Development", icon: Code },
] as const;

const fpmTeamNav = [
  { to: "/teams/fpm", label: "Leadership Team", icon: Users },
  { to: "/teams/fpm/production", label: "Stone Setting Team", icon: Factory },
  { to: "/teams/fpm/operation", label: "Metal Working Team", icon: ClipboardCheck },
  { to: "/teams/fpm/development", label: "Quality Control Team", icon: Code },
] as const;

const teamGroups = [
  { key: "fp", label: "FP Team", icon: Briefcase, nav: fpTeamNav },
  { key: "created", label: "Created Team", icon: ShoppingCart, nav: createdTeamNav },
  { key: "fpm", label: "FPM Team", icon: Factory, nav: fpmTeamNav },
] as const;

const marketingNav: readonly NavItem[] = [
  { to: "/marketing/social/fp/instagram", label: "FP Instagram", icon: Megaphone },
  { to: "/marketing/social/fp/tiktok", label: "FP TikTok", icon: Megaphone },
  { to: "/marketing/social/cb/instagram", label: "CB Instagram", icon: Megaphone },
  { to: "/marketing/social/fpf/instagram", label: "FPF Instagram", icon: Megaphone },
];

// Consultant Hub: an overview link, then each division (its own page) with its
// territories nested underneath (from lib/territories).
const consultantTerritoryNav: readonly NavItem[] = [
  { to: "/consultants/territories", label: "All territories", icon: MapIcon },
  ...[...new Set(TERRITORIES.map((t) => t.division))].map((d) => ({
    to: `/consultants/division/${d.toLowerCase()}`,
    label: `${d} division`,
    icon: Network,
    children: TERRITORIES.filter((t) => t.division === d).map((t) => ({
      to: `/consultants/territory/${t.key}`,
      label: t.label,
      icon: MapPin,
    })),
  })),
];

const marketingPaidNav: readonly NavItem[] = [
  { to: "/marketing/paid/fp", label: "FP Paid Ads", icon: DollarSign },
  { to: "/marketing/paid/cb", label: "CB Paid Ads", icon: DollarSign },
];

const hrChildren = [
  { to: "/hr/recruitment", label: "Recruitment", icon: UserPlus },
  { to: "/hr/onboarding", label: "Onboarding", icon: UserCheck },
  { to: "/hr/succession-planning", label: "Succession Planning", icon: TrendingUp },
] as const;

const adminNav: readonly NavItem[] = [
  { to: "/users", label: "User Management", icon: Users },
  { to: "/owners", label: "Owners Overview", icon: ClipboardList },
  { to: "/hr", label: "Human Resources", icon: UserCircle, children: hrChildren },
  { to: "/targets", label: "Targets", icon: Target },
  { to: "/campuses", label: "Campuses", icon: GraduationCap },
  { to: "/expenses", label: "Expenses", icon: DollarSign },
];

const hubspotNav = [
  { to: "/deals", label: "Deals", icon: Handshake },
  { to: "/metrics", label: "Metric Definitions", icon: Gauge },
  { to: "/data-sync", label: "Data Sync", icon: Database },
] as const;

const shopifyNav = [
  { to: "/shopify-orders", label: "Shopify Orders", icon: ShoppingCart },
] as const;

const developNav = [{ to: "/database-browser", label: "Database", icon: Database }] as const;

// biome-ignore lint/suspicious/noExplicitAny: nav item icon type
type NavItem = { to: string; label: string; icon: any; children?: readonly NavItem[] };

function NestedNavItem({
  item,
  collapsed,
  currentPath,
  renderLink,
}: {
  item: NavItem;
  collapsed: boolean;
  currentPath: string;
  renderLink: (item: NavItem) => React.ReactNode;
}) {
  const children = item.children ?? [];
  const active =
    currentPath === item.to ||
    currentPath.startsWith(`${item.to}/`) ||
    children.some((c) => currentPath === c.to || currentPath.startsWith(`${c.to}/`));
  const [open, setOpen] = useState(active);

  if (collapsed) {
    return <>{renderLink(item)}</>;
  }

  return (
    <div>
      <div className="flex items-center">
        <Link
          to={item.to}
          className={cn(
            "flex flex-1 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
            currentPath === item.to || currentPath.startsWith(`${item.to}/`)
              ? "border-l-2 border-brand-gold bg-white/10 text-white"
              : "text-white/60 hover:text-white/90 hover:bg-white/5",
          )}
        >
          <item.icon className="h-4 w-4 shrink-0" />
          {item.label}
        </Link>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="shrink-0 rounded-md p-2 text-white/40 hover:text-white/80 hover:bg-white/5"
        >
          {open ? (
            <ChevronDown className="h-4 w-4 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0" />
          )}
        </button>
      </div>
      {open && <div className="mt-1 space-y-1 pl-4">{children.map(renderLink)}</div>}
    </div>
  );
}

function TeamGroupNav({
  label,
  icon: Icon,
  items,
  collapsed,
  currentPath,
  renderLink,
}: {
  label: string;
  // biome-ignore lint/suspicious/noExplicitAny: icon component type
  icon: any;
  items: readonly NavItem[];
  collapsed: boolean;
  currentPath: string;
  renderLink: (item: NavItem) => React.ReactNode;
}) {
  const active = items.some(
    (item) =>
      currentPath === item.to ||
      currentPath.startsWith(`${item.to}/`) ||
      (item.children ?? []).some((c) => currentPath === c.to || currentPath.startsWith(`${c.to}/`)),
  );
  const [open, setOpen] = useState(active);

  const renderItem = (item: NavItem) =>
    item.children ? (
      <NestedNavItem
        key={item.to}
        item={item}
        collapsed={collapsed}
        currentPath={currentPath}
        renderLink={renderLink}
      />
    ) : (
      renderLink(item)
    );

  if (collapsed) {
    return <>{items.map(renderItem)}</>;
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          active ? "text-white" : "text-white/60 hover:text-white/90 hover:bg-white/5",
        )}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left">{label}</span>
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0" />
        )}
      </button>
      {open && <div className="mt-1 space-y-1 pl-4">{items.map(renderItem)}</div>}
    </div>
  );
}

export function SidebarNav({
  onNavigate,
  ownerId,
}: {
  onNavigate?: () => void;
  ownerId?: string | null;
}) {
  const { data: session } = useSession();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  // biome-ignore lint/suspicious/noExplicitAny: better-auth session type doesn't expose role
  const isAdmin = (session?.user as any)?.role === "admin";
  // Guests (contractors) see only the exact sub-team links they were granted.
  const guestViewer = isGuest(session?.user?.email);
  const allowedPaths = !isAdmin ? getAllowedTeamPaths(ownerId ?? "", session?.user?.email) : null;

  // For non-admins: only show team group if at least one of its links is allowed
  const canSeeTeamPath = (path: string) =>
    isAdmin || (allowedPaths?.some((p) => p === path || p.startsWith(`${path}/`)) ?? false);

  const { collapsed } = useSidebar();
  const user = session?.user;

  const [hasReports, setHasReports] = useState(false);
  const [grantedDashboards, setGrantedDashboards] = useState<string[]>([]);
  const userId = user?.id;
  useEffect(() => {
    if (isAdmin || !userId) return;
    fetch("/api/org-chart/has-reports")
      .then((res) => (res.ok ? res.json() : { hasReports: false }))
      .then((data) => setHasReports(data.hasReports))
      .catch(() => setHasReports(false));
    fetch("/api/me/dashboard-grants")
      .then((res) => (res.ok ? res.json() : { dashboards: [] }))
      .then((data) => setGrantedDashboards(data.dashboards ?? []))
      .catch(() => setGrantedDashboards([]));
  }, [isAdmin, userId]);

  const canSeeMarketing = isAdmin || grantedDashboards.includes("marketing");
  const canSeeConsultants = isAdmin || grantedDashboards.includes("consultant");

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((n: string) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  const handleLogout = async () => {
    await signOut();
    navigate({ to: "/" });
  };

  const renderLink = (item: { to: string; label: string; icon: any }) => {
    const isActive = currentPath === item.to || currentPath.startsWith(`${item.to}/`);
    return (
      <Link
        key={item.to}
        to={item.to}
        onClick={onNavigate}
        className={cn(
          "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          collapsed && "justify-center px-0",
          isActive
            ? "border-l-2 border-brand-gold bg-white/10 text-white"
            : "text-white/60 hover:text-white/90 hover:bg-white/5",
        )}
      >
        <item.icon className="h-4 w-4 shrink-0" />
        {!collapsed && item.label}
      </Link>
    );
  };

  return (
    <>
      <nav className="flex-1 space-y-1 p-3 overflow-y-auto">
        {!collapsed && (
          <div className="text-[10px] tracking-widest text-white/30 uppercase px-3 mb-1 mt-1">
            Main
          </div>
        )}
        {(isAdmin ? adminMainNav : userMainNav).map(renderLink)}
        {!isAdmin && hasReports && renderLink(orgChartNavItem)}
        {(isAdmin ||
          (user?.email &&
            new Set([
              "awillard@faithfulplatform.com",
              "jpolson@faithfulplatform.com",
              "nspalding@faithfulplatform.com",
            ]).has(user.email.toLowerCase()))) &&
          renderLink({ to: "/territories", label: "Territories", icon: MapIcon })}

        {!collapsed && (
          <div className="text-[10px] tracking-widest text-white/30 uppercase px-3 mb-1 mt-4">
            Teams
          </div>
        )}
        {teamGroups.map((group) => {
          const visibleItems = group.nav.filter((item) => canSeeTeamPath(item.to));
          if (!isAdmin && visibleItems.length === 0) return null;
          return (
            <TeamGroupNav
              key={group.key}
              label={group.label}
              icon={group.icon}
              items={
                isAdmin
                  ? group.nav
                  : ((guestViewer
                      ? visibleItems.map((item) =>
                          "children" in item
                            ? {
                                ...item,
                                children: item.children.filter((c) => allowedPaths?.includes(c.to)),
                              }
                            : item,
                        )
                      : visibleItems) as unknown as typeof group.nav)
              }
              collapsed={collapsed}
              currentPath={currentPath}
              renderLink={renderLink}
            />
          );
        })}

        {canSeeConsultants && (
          <>
            {!collapsed && (
              <div className="text-[10px] tracking-widest text-white/30 uppercase px-3 mb-1 mt-4">
                Consultants
              </div>
            )}
            {renderLink({ to: "/consultants/summary", label: "Executive Summary", icon: Crown })}
            <TeamGroupNav
              label="Territories"
              icon={MapIcon}
              items={consultantTerritoryNav}
              collapsed={collapsed}
              currentPath={currentPath}
              renderLink={renderLink}
            />
          </>
        )}

        {canSeeMarketing && (
          <>
            {!collapsed && (
              <div className="text-[10px] tracking-widest text-white/30 uppercase px-3 mb-1 mt-4">
                Marketing
              </div>
            )}
            {renderLink({ to: "/marketing/summary", label: "Executive Summary", icon: Crown })}
            <TeamGroupNav
              label="Social Media"
              icon={Megaphone}
              items={marketingNav}
              collapsed={collapsed}
              currentPath={currentPath}
              renderLink={renderLink}
            />
            <TeamGroupNav
              label="Paid Ads"
              icon={DollarSign}
              items={marketingPaidNav}
              collapsed={collapsed}
              currentPath={currentPath}
              renderLink={renderLink}
            />
            {renderLink({ to: "/marketing/web", label: "Website Analytics", icon: Globe })}
            {renderLink({ to: "/marketing/benchmarks", label: "Benchmarks", icon: Target })}
          </>
        )}

        {isAdmin && (
          <>
            {!collapsed && (
              <div className="text-[10px] tracking-widest text-white/30 uppercase px-3 mb-1 mt-4">
                Admin
              </div>
            )}
            {adminNav.map((item) =>
              item.children ? (
                <NestedNavItem
                  key={item.to}
                  item={item}
                  collapsed={collapsed}
                  currentPath={currentPath}
                  renderLink={renderLink}
                />
              ) : (
                renderLink(item)
              ),
            )}

            {!collapsed && (
              <div className="text-[10px] tracking-widest text-white/30 uppercase px-3 mb-1 mt-4">
                HubSpot
              </div>
            )}
            {hubspotNav.map(renderLink)}

            {!collapsed && (
              <div className="text-[10px] tracking-widest text-white/30 uppercase px-3 mb-1 mt-4">
                Shopify
              </div>
            )}
            {shopifyNav.map(renderLink)}
          </>
        )}

        {isAdmin && (
          <>
            {!collapsed && (
              <div className="text-[10px] tracking-widest text-white/30 uppercase px-3 mb-1 mt-4">
                Develop
              </div>
            )}
            {developNav.map(renderLink)}
          </>
        )}

        {isAdmin && renderLink({ to: "/settings", label: "Settings", icon: Settings })}
      </nav>

      <Separator className="bg-white/10" />

      {/* User info mini-card */}
      <div className={cn("p-3", collapsed && "px-2")}>
        {!collapsed ? (
          <div className="flex items-center gap-3 rounded-md px-3 py-2 mb-1">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-gold/20 text-brand-gold text-xs font-semibold">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <Badge
                variant="outline"
                className="mt-0.5 text-[10px] border-brand-gold/30 text-brand-gold px-1.5 py-0"
              >
                {/* biome-ignore lint/suspicious/noExplicitAny: better-auth session type */}
                {(user as any)?.role || "user"}
              </Badge>
            </div>
          </div>
        ) : (
          <div className="flex justify-center mb-1">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-gold/20 text-brand-gold text-xs font-semibold">
              {initials}
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          className={cn(
            "w-full text-white/60 hover:text-white/90 hover:bg-white/5",
            collapsed ? "justify-center px-0" : "justify-start gap-3",
          )}
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4" />
          {!collapsed && "Logout"}
        </Button>
      </div>
    </>
  );
}

export function Sidebar({ ownerId }: { ownerId?: string | null }) {
  const { collapsed, setCollapsed } = useSidebar();

  return (
    <aside
      className={cn(
        "hidden lg:flex h-screen flex-col bg-sidebar text-sidebar-foreground transition-[width] duration-300",
        collapsed ? "w-16" : "w-64",
      )}
    >
      {/* Header */}
      <div
        className={cn(
          "flex h-16 items-center shrink-0",
          collapsed ? "justify-center px-2" : "justify-between px-6",
        )}
      >
        {!collapsed && (
          <span className="font-serif text-lg text-white tracking-tight">Faithful Platform</span>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-white/40 hover:text-white/80 hover:bg-white/5"
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
        </Button>
      </div>

      <Separator className="bg-white/10" />

      <SidebarNav ownerId={ownerId} />
    </aside>
  );
}
